import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, MessageCircle, Calendar, Heart, Settings, User, LogOut, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { to: '/dashboard/find', label: 'Find', icon: Search },
  { to: '/dashboard/chats', label: 'Chats', icon: MessageCircle },
  { to: '/dashboard/bookings', label: 'Bookings', icon: Calendar },
  { to: '/dashboard/favourites', label: 'Likes', icon: Heart },
  { to: '/dashboard/fwb', label: 'FWB', icon: Crown },
];

const DashboardLayout = () => {
  const { userProfile, signOut } = useAuth();
  const location = useLocation();
  const isProvider = userProfile?.role === 'service_provider';

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 flex flex-col font-sans">
      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-50 bg-[#0f172a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/st69_logo_new.png" alt="Stranger69" className="h-12 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="flex items-center bg-slate-900/50 p-1.5 rounded-2xl border border-white/5 shadow-inner">
              {navItems.map((item) => {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="relative px-5 py-2.5 rounded-xl transition-all group"
                  >
                    {active && (
                      <motion.div
                        layoutId="nav-glow"
                        className="absolute inset-0 bg-brand-pink/10 rounded-xl border border-brand-pink/20 shadow-[0_0_20px_rgba(236,72,153,0.1)]"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <div className="relative flex items-center gap-2.5">
                      <item.icon className={`h-4 w-4 transition-colors ${active ? 'text-brand-pink' : 'text-slate-400 group-hover:text-white'}`} />
                      <span className={`text-sm font-bold transition-colors ${active ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}>
                        {item.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </nav>

            {/* User Profile Area */}
            <div className="flex items-center gap-4">
              {userProfile?.subscriptionStatus === 'premium' && (
                <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-widest">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Premium
                </div>
              )}
              
              <Link 
                to="/dashboard/account" 
                className={`flex items-center gap-3 p-1 rounded-2xl border transition-all ${
                  isActive('/dashboard/account') ? 'bg-brand-pink/10 border-brand-pink/20' : 'bg-slate-800/50 border-white/5 hover:border-white/20'
                }`}
              >
                <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10 shadow-lg">
                  {userProfile?.avatar_url ? (
                    <img src={userProfile.avatar_url} className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-5 w-5 text-slate-500" />
                  )}
                </div>
                <div className="pr-3 hidden lg:block">
                   <p className="text-xs font-black text-white leading-tight">{userProfile?.displayName?.split(' ')[0] || 'Member'}</p>
                   <p className="text-[10px] text-slate-500 uppercase tracking-tighter font-bold">My Account</p>
                </div>
              </Link>

              <button
                onClick={signOut}
                className="w-10 h-10 rounded-xl bg-slate-800/50 border border-white/5 flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-all"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header (Minimal) */}
      <header className="md:hidden sticky top-0 z-50 bg-[#0f172a]/90 backdrop-blur-lg border-b border-white/5 px-4 h-16 flex items-center justify-between">
        <Link to="/">
          <img src="/st69_logo_new.png" alt="Logo" className="h-9 w-auto" />
        </Link>
        <div className="flex items-center gap-3">
          {userProfile?.subscriptionStatus === 'premium' && (
            <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]" />
          )}
          <Link to="/dashboard/account" className="w-10 h-10 rounded-xl bg-slate-800 border border-white/10 overflow-hidden">
            {userProfile?.avatar_url ? (
              <img src={userProfile.avatar_url} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><User className="h-5 w-5 text-slate-500" /></div>
            )}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-6 left-4 right-4 z-50">
        <div className="bg-[#1e293b]/95 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] px-4 py-3 flex items-center justify-around">
          {navItems.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className="relative flex flex-col items-center gap-1 group"
              >
                <div className={`p-2.5 rounded-2xl transition-all duration-300 ${
                  active ? 'bg-brand-pink text-white shadow-[0_0_20px_rgba(236,72,153,0.4)] scale-110 -translate-y-1' : 'text-slate-400'
                }`}>
                  <item.icon className="h-6 w-6" />
                </div>
                <span className={`text-[10px] font-bold transition-all ${active ? 'text-white opacity-100' : 'text-slate-500 opacity-0'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          <Link to="/dashboard/settings" className="p-2.5 text-slate-400">
             <Settings className="h-6 w-6" />
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
